import { createContext, useContext, ReactNode } from 'react';
import { useToast } from '@/hooks/useToast';
import { ToastContainer } from '@/components/ui/Toast';
import { soundService } from '@/services/SoundService';

interface ToastContextType {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const { toasts, removeToast, success, error, warning, info } = useToast();

  const wrappedSuccess = (title: string, message?: string) => {
    soundService.playSuccess();
    success(title, message);
  };

  const wrappedError = (title: string, message?: string) => {
    soundService.playError();
    error(title, message);
  };

  const wrappedWarning = (title: string, message?: string) => {
    soundService.playPop();
    warning(title, message);
  };

  const wrappedInfo = (title: string, message?: string) => {
    soundService.playTick();
    info(title, message);
  };

  return (
    <ToastContext.Provider value={{ success: wrappedSuccess, error: wrappedError, warning: wrappedWarning, info: wrappedInfo }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useGlobalToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useGlobalToast must be used within a ToastProvider');
  }
  return context;
};